/**
 * Admin API 用 Zod スキーマ
 *
 * types/admin.ts の各 interface に対応するランタイムバリデーション用スキーマ。
 * 型定義は z.infer で自動生成し、interface と一致させる。
 */
import { z } from 'zod';
// ========================================
// Admin ドメインエンティティスキーマ
// ========================================
/**
 * 使用量の期間
 */
export const AdminUsagePeriodSchema = z.object({
    year: z.number(),
    month: z.number(),
});
/**
 * 日別使用量データ
 */
export const AdminDailyUsageDataSchema = z.object({
    pk: z.string(),
    sk: z.string(),
    date: z.string(),
    inputTokens: z.number(),
    outputTokens: z.number(),
    cacheReadTokens: z.number().optional(),
    cacheWriteTokens: z.number().optional(),
    totalTokens: z.number(),
    cost: z.number(),
    requestCount: z.number(),
    model: z.string().optional(),
});
/**
 * 月別使用量データ
 */
export const AdminMonthlyUsageDataSchema = z.object({
    pk: z.string(),
    sk: z.string(),
    month: z.string().optional(),
    inputTokens: z.number(),
    outputTokens: z.number(),
    cacheReadTokens: z.number().optional(),
    cacheWriteTokens: z.number().optional(),
    totalTokens: z.number(),
    cost: z.number(),
    requestCount: z.number(),
    model: z.string().optional(),
});
/**
 * 利用回数ランキング項目
 */
export const AdminUsageRankingItemSchema = z.object({
    email: z.string(),
    userName: z.string(),
    requestCount: z.number(),
    totalTokens: z.number(),
    cost: z.number(),
});
/**
 * ユーザー統計
 */
export const AdminUserStatsSchema = z.object({
    activeUsers: z.number(),
    totalUsers: z.number(),
    activeRate: z.number(),
});
/**
 * 利用可能なモデル情報
 */
export const AdminAvailableModelSchema = z.object({
    modelId: z.string(),
    displayName: z.string(),
});
/**
 * モデル別使用量データ（円グラフ用）
 */
export const AdminModelUsageSchema = z.object({
    modelId: z.string(),
    displayName: z.string(),
    cost: z.number(),
    requestCount: z.number(),
});
/**
 * Cognitoユーザー情報
 */
export const AdminCognitoUserSchema = z.object({
    email: z.string(),
    emailVerified: z.boolean(),
    status: z.string(),
    enabled: z.boolean(),
    createdAt: z.number().nullable(),
    lastLogin: z.number().nullable(),
});
/**
 * タスクサマリー（管理用）
 */
export const AdminTaskSummarySchema = z.object({
    id: z.string(),
    title: z.string(),
    userEmail: z.string(),
    createdAt: z.number(),
    updatedAt: z.number(),
    messageCount: z.number(),
    deleted: z.boolean(),
    deletedAt: z.number().optional(),
    workspaceId: z.string().optional(),
    workspaceName: z.string().optional(),
    totalTokens: z.number().optional(),
    cost: z.number().optional(),
    latestVersion: z.number().optional(),
    versionCount: z.number().optional(),
    totalCost: z.number().optional(),
});
/**
 * タスクバージョン情報（管理用）
 * Admin版: status は string（api-schemas 版は TaskStatusSchema）
 */
export const AdminTaskVersionInfoSchema = z.object({
    version: z.number(),
    createdAt: z.number(),
    status: z.string(),
    totalTokens: z.number().optional(),
    cost: z.number().optional(),
});
/**
 * メッセージ情報（管理用）
 */
export const AdminMessageInfoSchema = z.object({
    id: z.string(),
    role: z.string(),
    content: z.string().optional(),
    parts: z.array(z.unknown()).optional(),
    createdAt: z.number(),
    modelInfo: z
        .object({
        provider: z.string(),
        modelId: z.string(),
    })
        .optional(),
    usage: z
        .object({
        inputTokens: z.number(),
        outputTokens: z.number(),
        totalTokens: z.number(),
        cost: z.number().optional(),
        reasoningTokens: z.number().optional(),
        cacheReadTokens: z.number().optional(),
        cacheWriteTokens: z.number().optional(),
    })
        .optional(),
    duration: z.number().optional(),
});
/**
 * タスク詳細（管理用）
 */
export const AdminTaskDetailSchema = z.object({
    id: z.string(),
    title: z.string(),
    userEmail: z.string(),
    createdAt: z.number(),
    updatedAt: z.number(),
    deleted: z.boolean(),
    workspaceId: z.string().optional(),
    workspaceName: z.string().optional(),
    messages: z.array(AdminMessageInfoSchema),
    totalTokens: z.number().optional(),
    cost: z.number().optional(),
});
// ========================================
// Admin API レスポンススキーマ
// ========================================
/**
 * GET /admin/usage レスポンス
 */
export const GetAdminUsageResponseSchema = z.object({
    period: AdminUsagePeriodSchema,
    dailyData: z.array(AdminDailyUsageDataSchema),
    monthlyData: z.array(AdminMonthlyUsageDataSchema),
    monthlyTrend: z.array(AdminMonthlyUsageDataSchema),
    ranking: z.array(AdminUsageRankingItemSchema),
    userStats: AdminUserStatsSchema,
    availableModels: z.array(AdminAvailableModelSchema),
    modelUsage: z.array(AdminModelUsageSchema),
});
/**
 * GET /admin/usage/user/{email} レスポンス
 */
export const GetAdminUserUsageResponseSchema = GetAdminUsageResponseSchema.extend({
    email: z.string(),
});
/**
 * GET /admin/users レスポンス
 */
export const ListAdminUsersResponseSchema = z.object({
    users: z.array(AdminCognitoUserSchema),
    total: z.number(),
});
/**
 * GET /admin/tasks レスポンス
 */
export const ListAdminTasksResponseSchema = z.object({
    tasks: z.array(AdminTaskSummarySchema),
    total: z.number(),
});
/**
 * GET /admin/users/{email}/tasks/{taskId} レスポンス
 */
export const GetAdminTaskDetailResponseSchema = z.object({
    task: AdminTaskDetailSchema,
});
/**
 * GET /admin/users/{email}/tasks/{taskId}/versions レスポンス
 */
export const ListAdminTaskVersionsResponseSchema = z.object({
    versions: z.array(AdminTaskVersionInfoSchema),
});
//# sourceMappingURL=admin-schemas.js.map